import React, { useState, useEffect } from 'react';
import AppIcon from '../common/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import Pagination from '../common/Pagination';

// 🟢 [修复] dueDate/submittedAt 可能是 Firestore Timestamp ({_seconds})、ISO 字符串、
// 数字或缺失。统一转成 Date（无法解析返回 null），避免各处直接读 _seconds 崩溃/白屏。
const firestoreToDate = (v) => {
  if (!v) return null;
  if (typeof v === 'object' && typeof v._seconds === 'number') return new Date(v._seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
// 转 <input type="date"> 需要的 'YYYY-MM-DD'，无效日期返回 ''
const toDateInputValue = (v) => {
  const d = firestoreToDate(v);
  return d ? d.toISOString().split('T')[0] : '';
};

const TeacherPage = () => {
  const { user } = useAuth();
  
  // ==========================================
  // 1. 状态管理
  // ==========================================
  const [activeTab, setActiveTab] = useState('classes');
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // 业务数据
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [vocabList, setVocabList] = useState([]); 
  const [vocabMap, setVocabMap] = useState({});
  const [schoolName, setSchoolName] = useState('');
  
  const [currentAssignmentSubmissions, setCurrentAssignmentSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  // 分页状态
  const CLASS_ITEMS_PER_PAGE = 8;
  const ASSIGNMENT_ITEMS_PER_PAGE = 5;
  const STUDENT_ITEMS_PER_PAGE = 10;

  const [classPage, setClassPage] = useState(1);
  const [studentPage, setStudentPage] = useState(1);
  const [assignmentPage, setAssignmentPage] = useState(1);

  // 筛选状态
  const [assignmentClassFilter, setAssignmentClassFilter] = useState('all');

  // 弹窗控制
  const [isClassModalOpen, setIsClassModalOpen] = useState(false);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [viewingAssignment, setViewingAssignment] = useState(null);
  const [viewingClass, setViewingClass] = useState(null);

  // 提交状态 (Loading)
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirm Dialog
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, type: null, id: null, title: '', message: '', isDestructive: false
  });
  const [isActionLoading, setIsActionLoading] = useState(false);

  // 表单数据
  const [classForm, setClassForm] = useState({ name: '', description: '', academicYear: '2025' });
  const [assignmentForm, setAssignmentForm] = useState({
    title: '', classId: '', dueDate: '', vocabularyLevel: 'core', instructions: '', selectedWordIds: []
  });
  const [wordSearchTerm, setWordSearchTerm] = useState('');

  const [editClassForm, setEditClassForm] = useState({ name: '', description: '', academicYear: '2025' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [tempClassStudents, setTempClassStudents] = useState([]); 

  // 🟢 [新增] 学生搜索联想状态
  const [studentSuggestions, setStudentSuggestions] = useState([]);
  const [isSearchingStudents, setIsSearchingStudents] = useState(false);

  // 🟢 [新增] 动态计算年份选项：去年、今年、明年
  const currentYear = new Date().getFullYear();
  const academicYearOptions = [currentYear - 1, currentYear, currentYear + 1];

  // ==========================================
  // 2. 数据加载
  // ==========================================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classesData, assignmentsData, studentsData, vocabRes] = await Promise.all([
          apiService.getClasses(),
          apiService.getAssignments(),
          apiService.getAllUsers({ role: 'student', limit: 2000 }), 
          apiService.getVocabulary({ limit: 1000 })
        ]);

        if (user?.schoolId) {
             apiService.client.get(`/schools/${user.schoolId}`)
                .then(res => setSchoolName(res.name))
                .catch(() => setSchoolName('Greenwood Primary'));
        } else {
            setSchoolName('Greenwood Primary');
        }

        setClasses(classesData.classes || []);
        setAssignments(assignmentsData.assignments || []);

        const vMap = {};
        const vList = vocabRes.vocabulary || [];
        vList.forEach(v => { vMap[v.id] = v.word; });
        setVocabMap(vMap);
        setVocabList(vList);

        const myClassIds = (classesData.classes || []).map(c => c.id);
        const rawStudents = studentsData.users || [];
        
        const myStudents = rawStudents
          .filter(s => s.classIds && Array.isArray(s.classIds) && s.classIds.some(id => myClassIds.includes(id)))
          .map(s => {
            const studentClassId = s.classIds.find(id => myClassIds.includes(id));
            const studentClass = (classesData.classes || []).find(c => c.id === studentClassId);
            return {
              ...s,
              className: studentClass ? studentClass.name : 'Unassigned',
              lastActive: s.lastActiveAt ? new Date(s.lastActiveAt._seconds * 1000).toLocaleDateString() : 'Never',
              wordsLearned: s.points ? Math.floor(s.points / 10) : 0, 
              accuracy: s.overallAccuracy || 0
            };
          });
        
        setStudents(myStudents);
        window.allStudentsCache = rawStudents;

      } catch (error) {
        console.error('Failed to load teacher data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, refreshTrigger]);

  useEffect(() => {
    if (viewingAssignment) {
        const fetchSubmissions = async () => {
            setLoadingSubmissions(true);
            try {
                const res = await apiService.client.get(`/assignments/${viewingAssignment.id}/submissions`);
                setCurrentAssignmentSubmissions(res.submissions || []);
            } catch (e) {
                console.warn("Could not fetch submissions", e);
                setCurrentAssignmentSubmissions([]);
            } finally {
                setLoadingSubmissions(false);
            }
        };
        fetchSubmissions();
    }
  }, [viewingAssignment]);

  // 🟢 [修复版] 监听 inviteEmail 输入，触发搜索
  // 解决了“竞态问题”：防止旧的慢请求覆盖新的快请求
  useEffect(() => {
    // 1. 如果输入太短，直接清空并返回
    if (!inviteEmail || inviteEmail.length < 4) {
      setStudentSuggestions([]);
      return;
    }

    // 🚩 标志位：标记当前这一轮 Effect 是否有效
    let isActive = true;

    const timer = setTimeout(async () => {
      // 在开始新搜索前，先标记正在搜索（这会让 UI 显示 Loading，隐藏旧结果）
      if (isActive) setIsSearchingStudents(true);

      try {
        const res = await apiService.getAllUsers({ 
          role: 'student', 
          status: 'approved', 
          search: inviteEmail,
          limit: 5 
        });
        
        // 🛑 核心修复：只有当当前 Effect 依然活跃时，才更新 State
        // 如果用户已经输入了新字符，isActive 会变成 false，这里的逻辑就会被跳过
        if (isActive) {
            const currentIds = tempClassStudents.map(s => s.id);
            const filtered = (res.users || []).filter(u => !currentIds.includes(u.id));
            setStudentSuggestions(filtered);
        }
      } catch (error) {
        console.error("Search failed", error);
        // 出错时也要清空建议，防止显示旧数据
        if (isActive) setStudentSuggestions([]); 
      } finally {
        if (isActive) setIsSearchingStudents(false);
      }
    }, 300);

    // 🧹 清理函数：当 inviteEmail 变化（用户再次输入）时执行
    return () => {
      isActive = false; // ❌ 废弃上一次的请求
      clearTimeout(timer);
    };
  }, [inviteEmail, tempClassStudents]);

  // ==========================================
  // 3. 辅助函数
  // ==========================================

  const getClassNameById = (classId) => {
    const found = classes.find(c => c.id === classId);
    return found ? found.name : 'Unknown Class';
  };

  const getClassStudentCountById = (classId) => {
    const found = classes.find(c => c.id === classId);
    return found ? (found.studentCount || 0) : 0;
  };

  const getOverviewStats = () => {
    const totalStudents = students.length;
    const totalAcc = students.reduce((acc, s) => acc + (s.accuracy || 0), 0);
    const avgAccuracy = totalStudents > 0 ? Math.round(totalAcc / totalStudents) : 0;
    const activeAssignments = assignments.filter(a => {
        const d = firestoreToDate(a.dueDate);
        return d && d >= new Date();
    }).length;
    const wordsAssigned = assignments.reduce((acc, a) => acc + (a.vocabularyIds ? a.vocabularyIds.length : 0), 0);
    return { totalStudents, avgAccuracy, activeAssignments, wordsAssigned };
  };

  const getClassStats = (classId) => {
    const classStudents = students.filter(s => s.classIds && s.classIds.includes(classId));
    const totalProgress = classStudents.reduce((acc, s) => acc + (s.accuracy || 0), 0);
    const avgProgress = classStudents.length > 0 ? Math.round(totalProgress / classStudents.length) : 0;
    const activeAssigns = assignments.filter(a => a.classId === classId && new Date(a.dueDate) >= new Date()).length;
    return { count: classStudents.length, avgProgress, activeAssigns };
  };

  // --- Handlers ---

  const handleOpenEditClass = (cls) => {
    setEditingClass(cls);
    setEditClassForm({ 
        name: cls.name, 
        description: cls.description || '', 
        academicYear: cls.academicYear || '2025' 
    });
    const currentStudents = students.filter(s => s.classIds && s.classIds.includes(cls.id));
    setTempClassStudents([...currentStudents]);
    setInviteEmail('');
  };

  const handleAddStudentToTempList = () => {
    const email = inviteEmail.trim();
    if (!email) return;
    if (tempClassStudents.length >= 50) return alert('Class limit reached.');
    
    // 检查是否已经在列表中
    if (tempClassStudents.find(s => s.email.toLowerCase() === email.toLowerCase())) {
        return alert('Student already in class.');
    }

    // 从全量缓存中查找 (兜底逻辑)
    const targetStudent = window.allStudentsCache?.find(s => s.email.toLowerCase() === email.toLowerCase());
    
    if (!targetStudent) {
        return alert('User not found. Ensure student is registered.');
    }

    // 🟢 关键修改：检查用户状态
    if (targetStudent.status !== 'approved') {
        return alert(`Cannot add this student. Current status: ${targetStudent.status} (Must be 'approved').`);
    }

    setTempClassStudents(prev => [...prev, targetStudent]);
    setInviteEmail('');
    setStudentSuggestions([]); // 清空建议
  };

  const handleRemoveFromTempList = (studentId) => {
    setTempClassStudents(prev => prev.filter(s => s.id !== studentId));
  };

  const handleSaveEditClass = async () => {
    if (isSubmitting || !editingClass) return;
    setIsSubmitting(true);
    try {
      const studentIds = tempClassStudents.map(s => s.id);
      await apiService.client.put(`/classes/${editingClass.id}`, {
        ...editClassForm,
        studentIds: studentIds, 
        studentCount: studentIds.length
      });
      setRefreshTrigger(prev => prev + 1);
      setEditingClass(null);
    } catch (error) { alert('Failed: ' + error.message); } finally { setIsSubmitting(false); }
  };

  const openConfirm = (type, id, title, message, isDestructive = false) => {
    setConfirmDialog({ isOpen: true, type, id, title, message, isDestructive });
  };

  const handleConfirmAction = async () => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    try {
      const { type, id } = confirmDialog;
      if (type === 'delete_assignment') await apiService.client.delete(`/assignments/${id}`);
      else if (type === 'delete_class') { await apiService.client.delete(`/classes/${id}`); setEditingClass(null); }
      setRefreshTrigger(prev => prev + 1);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    } catch (error) { alert(error.message); } finally { setIsActionLoading(false); }
  };

  const handleCreateClass = async (e) => { 
      e.preventDefault(); 
      if (isSubmitting) return; // Prevent double submit
      setIsSubmitting(true); 
      try { 
          await apiService.createClass({ ...classForm }); 
          setRefreshTrigger(p=>p+1); 
          setIsClassModalOpen(false); 
          setClassForm({ name: '', description: '', academicYear: '2025' }); 
      } catch(e){ 
          alert(e.message); 
      } finally { 
          setIsSubmitting(false); 
      } 
  };

  const handleSaveAssignment = async (e) => { 
      e.preventDefault(); 
      if (isSubmitting) return; // Prevent double submit
      setIsSubmitting(true); 
      try { 
          const payload = {
              title: assignmentForm.title,
              classId: assignmentForm.classId,
              dueDate: assignmentForm.dueDate,
              vocabularyLevel: assignmentForm.vocabularyLevel,
              instructions: assignmentForm.instructions,
              vocabularyIds: assignmentForm.selectedWordIds 
          };

          if (editingAssignment) {
              await apiService.client.put(`/assignments/${editingAssignment.id}`, payload);
          } else {
              await apiService.createAssignment(payload);
          }
          
          setRefreshTrigger(p=>p+1); 
          setIsAssignmentModalOpen(false); 
          setEditingAssignment(null);
          setAssignmentForm({ title: '', classId: '', dueDate: '', vocabularyLevel: 'core', instructions: '', selectedWordIds: [] });
      } catch(e){ alert(e.message); } finally { setIsSubmitting(false); } 
  };

  const toggleWordInAssignment = (wordId) => {
      setAssignmentForm(prev => {
          const exists = prev.selectedWordIds.includes(wordId);
          if (exists) {
              return { ...prev, selectedWordIds: prev.selectedWordIds.filter(id => id !== wordId) };
          } else {
              return { ...prev, selectedWordIds: [...prev.selectedWordIds, wordId] };
          }
      });
  };

  // ==========================================
  // 4. Renderers (UI Implementation)
  // ==========================================

  // View 1: My Classes
  const renderClassesView = () => {
    const overview = getOverviewStats();
    
    // 分页逻辑
    const totalItems = classes.length;
    const totalPages = Math.ceil(totalItems / CLASS_ITEMS_PER_PAGE);
    const currentClasses = classes.slice((classPage - 1) * CLASS_ITEMS_PER_PAGE, classPage * CLASS_ITEMS_PER_PAGE);

    return (
      <div className="space-y-8 animate-fade-in">
        
        {/* Quick Overview (Top) */}
        <div className="bg-white/60 backdrop-blur-lg rounded-2xl p-8 shadow-lg border border-white/40">
          <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center">
              <AppIcon name="chart-pie" className="mr-2 text-slate-600" /> Quick Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-gray-200/50">
            <div className="px-2">
                <div className="text-4xl font-bold text-slate-800 mb-1">{overview.totalStudents}</div>
                <div className="text-xs text-slate-500 uppercase font-bold tracking-wider">Total Students</div>
            </div>
            <div className="px-2">
                <div className="text-4xl font-bold text-slate-800 mb-1">{overview.avgAccuracy}%</div>
                <div className="text-xs text-slate-500 uppercase font-bold tracking-wider">Average Accuracy</div>
            </div>
            <div className="px-2">
                <div className="text-4xl font-bold text-slate-800 mb-1">{overview.activeAssignments}</div>
                <div className="text-xs text-slate-500 uppercase font-bold tracking-wider">Active Assignments</div>
            </div>
            <div className="px-2">
                <div className="text-4xl font-bold text-slate-800 mb-1">{overview.wordsAssigned}</div>
                <div className="text-xs text-slate-500 uppercase font-bold tracking-wider">Words Assigned</div>
            </div>
          </div>
        </div>

        {/* Add Button */}
        <div className="flex justify-end">
          <motion.button 
            whileHover={{ scale: 1.02 }} 
            onClick={() => {
                setClassForm({ name: '', description: '', academicYear: '2025' });
                setIsClassModalOpen(true);
            }} 
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-md flex items-center"
          > 
            <AppIcon name="plus" className="mr-2" /> Add New Class 
          </motion.button>
        </div>

        {/* Classes Cards: Grid of 8, 2 per row (md:grid-cols-2 only) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentClasses.map((classItem, index) => {
            const stats = getClassStats(classItem.id);
            const studentCount = classItem.studentCount || stats.count;

            return (
              <motion.div 
                key={classItem.id} 
                initial={{ scale: 0.95, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }} 
                transition={{ delay: index * 0.05 }} 
                className="bg-white/80 backdrop-blur-md rounded-2xl p-6 text-gray-800 shadow-md border border-white/60 hover:shadow-xl transition-shadow"
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-slate-800">{classItem.name}</h3>
                    <div className="text-gray-400 hover:text-red-500 cursor-pointer" onClick={() => openConfirm('delete_class', classItem.id, 'Delete', 'Delete class?', true)}>
                        <AppIcon name="trash-alt" className="text-sm" />
                    </div>
                </div>
                
                {/* Stats */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center text-sm font-medium">
                      <span className="text-slate-600">Students:</span>
                      <span className="font-bold text-slate-900">{studentCount}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm font-medium">
                      <span className="text-slate-600">Average Progress:</span>
                      <span className="font-bold text-slate-900">{stats.avgProgress}%</span>
                  </div>
                  
                  {/* Green Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-green-500 h-full rounded-full" style={{ width: `${stats.avgProgress}%` }}></div>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm font-medium pt-1">
                      <span className="text-slate-600">Active Assignments:</span>
                      <span className="font-bold text-slate-900">{stats.activeAssigns}</span>
                  </div>
                </div>

                {/* Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => setViewingClass(classItem)} 
                    className="bg-white border border-slate-200 text-blue-600 font-bold py-2 rounded-lg hover:bg-slate-50 transition-colors shadow-sm text-sm"
                  >
                    View Details
                  </button>
                  <button 
                    onClick={() => handleOpenEditClass(classItem)} 
                    className="bg-blue-500 text-white font-bold py-2 rounded-lg hover:bg-blue-600 transition-colors shadow-md text-sm"
                  >
                    Edit Class
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
            <div className="flex justify-center mt-4">
                <Pagination currentPage={classPage} totalPages={totalPages} onPageChange={setClassPage} totalItems={totalItems} />
            </div>
        )}
      </div>
    );
  };

  // View 2: Student Progress
  const renderStudentsView = () => {
    const totalItems = students.length;
    const totalPages = Math.ceil(totalItems / STUDENT_ITEMS_PER_PAGE);
    const currentStudents = students.slice((studentPage - 1) * STUDENT_ITEMS_PER_PAGE, studentPage * STUDENT_ITEMS_PER_PAGE);
    
    return (
        <div className="bg-white/40 backdrop-blur-lg rounded-3xl p-8 border border-white/40 shadow-xl min-h-[500px]">
            <div className="mb-6 flex justify-between items-center">
                <h2 className="text-xl font-bold text-slate-800 flex items-center">
                    <AppIcon name="chart-line" className="mr-2" /> Student Progress
                </h2>
            </div>
            
            <div className="space-y-4">
                {currentStudents.length > 0 ? currentStudents.map((student) => (
                <div key={student.id} className="bg-sky-50/80 rounded-xl p-5 flex flex-col md:flex-row justify-between items-center shadow-sm hover:shadow-md transition-all border border-sky-100">
                    <div className="mb-2 md:mb-0 w-full md:w-auto">
                        <h3 className="font-bold text-lg text-slate-800">{student.name}</h3>
                        <p className="text-sm text-slate-500 mt-0.5">{student.className}</p>
                        <p className="text-xs text-slate-400 mt-1">Last active: {student.lastActive}</p>
                    </div>
                    <div className="text-right flex flex-col items-end w-full md:w-auto">
                        <div className="font-bold text-3xl text-slate-800 leading-tight">
                            {student.wordsLearned}
                        </div>
                        <div className="text-xs text-slate-500 mb-2 font-medium">words learned</div>
                        <span className={`text-xs px-3 py-1 rounded-full text-white font-bold shadow-sm ${
                            student.accuracy >= 90 ? 'bg-green-500' : 'bg-yellow-500'
                        }`}>
                            {student.accuracy}% accuracy
                        </span>
                    </div>
                </div>
                )) : <div className="text-center text-slate-500 py-10">No students found.</div>}
            </div>
            {totalPages > 1 && (
                <div className="flex justify-center mt-8">
                    <Pagination currentPage={studentPage} totalPages={totalPages} onPageChange={setStudentPage} totalItems={totalItems} />
                </div>
            )}
        </div>
    );
  };

  // View 3: Assignments
  const renderAssignmentsView = () => {
    let filtered = assignments;
    if (assignmentClassFilter !== 'all') {
        filtered = assignments.filter(a => a.classId === assignmentClassFilter);
    }

    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / ASSIGNMENT_ITEMS_PER_PAGE);
    const currentAssignments = filtered.slice((assignmentPage - 1) * ASSIGNMENT_ITEMS_PER_PAGE, assignmentPage * ASSIGNMENT_ITEMS_PER_PAGE);

    return (
        <div className="space-y-6">
            
            {/* Filter & Add Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <span className="text-sm font-bold text-slate-600">Filter by Class:</span>
                    <select 
                        className="bg-white/50 border border-white px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        value={assignmentClassFilter}
                        onChange={(e) => { setAssignmentClassFilter(e.target.value); setAssignmentPage(1); }}
                    >
                        <option value="all">All Classes</option>
                        {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>

                <motion.button 
                    whileHover={{ scale: 1.05 }} 
                    onClick={() => {
                        setAssignmentForm({ title: '', classId: '', dueDate: '', vocabularyLevel: 'core', instructions: '', selectedWordIds: [] });
                        setIsAssignmentModalOpen(true);
                    }} 
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 px-6 rounded-lg shadow-lg flex items-center"
                >
                    <AppIcon name="plus" className="mr-2" /> Create Assignment
                </motion.button>
            </div>
            
            {currentAssignments.length > 0 ? currentAssignments.map((assignment) => {
                let dueDateStr = '';
                const rawDueDate = firestoreToDate(assignment.dueDate);
                if (rawDueDate) {
                    dueDateStr = rawDueDate.toLocaleDateString();
                }
                const isExpired = rawDueDate && rawDueDate < new Date();

                const wordTags = (assignment.vocabularyIds || []).map(id => vocabMap[id] || 'Word').slice(0, 6);
                const className = getClassNameById(assignment.classId);
                const totalStudents = getClassStudentCountById(assignment.classId);
                const completedCount = assignment.completedCount || 0;
                const progress = totalStudents > 0 ? (completedCount / totalStudents) * 100 : 0;

                return (
                    <motion.div 
                        key={assignment.id} 
                        initial={{ opacity: 0, y: 10 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        className="bg-sky-50/50 backdrop-blur-lg rounded-2xl text-gray-800 shadow-sm border border-sky-100 overflow-hidden"
                    >
                        {/* Header Section */}
                        <div className="p-6 pb-2">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800">{assignment.title}</h3>
                                    <p className="text-sm text-slate-500 mt-1 font-medium">{className}</p>
                                    <p className={`text-sm mt-1 font-medium ${isExpired ? 'text-red-500' : 'text-slate-400'}`}>
                                        Due: {dueDateStr}
                                        {isExpired && <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded ml-2 font-bold uppercase">Expired</span>}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span className="text-3xl font-bold text-slate-800">{completedCount}/{totalStudents}</span>
                                    <span className="block text-xs text-slate-500 uppercase font-bold mt-1">completed</span>
                                </div>
                            </div>
                        </div>

                        {/* Blue Progress Bar */}
                        <div className="px-6 py-2">
                            <div className="flex justify-between text-xs text-slate-500 mb-1 font-medium">
                                <span>Progress</span>
                                <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="w-full bg-white rounded-full h-2.5 overflow-hidden border border-blue-100">
                                <div className="bg-blue-500 h-full rounded-full transition-all duration-1000" style={{ width: `${progress}%` }}></div>
                            </div>
                        </div>

                        {/* Words Pills */}
                        <div className="px-6 py-4 mt-2">
                            <p className="text-sm text-slate-700 font-medium mb-2">Words in this assignment:</p>
                            <div className="flex flex-wrap gap-2">
                                {wordTags.map((w, i) => (
                                    <span key={i} className="bg-white border border-blue-100 px-3 py-1 rounded-full text-xs font-bold text-slate-600 shadow-sm">
                                        {w}
                                    </span>
                                ))}
                                {(assignment.vocabularyIds?.length || 0) > 6 && (
                                    <span className="text-xs text-slate-400 py-1 font-medium">+{assignment.vocabularyIds.length - 6} more</span>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="p-4 bg-white/50 border-t border-sky-100 flex gap-3">
                            <button 
                                onClick={() => setViewingAssignment(assignment)} 
                                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2.5 px-6 rounded-lg shadow-md transition-colors text-sm"
                            >
                                View Details
                            </button>
                            <button 
                                onClick={() => { 
                                    setEditingAssignment(assignment); 
                                    setAssignmentForm({
                                        title: assignment.title,
                                        classId: assignment.classId,
                                        dueDate: toDateInputValue(assignment.dueDate),
                                        vocabularyLevel: assignment.vocabularyLevel || 'core',
                                        instructions: assignment.instructions || '',
                                        selectedWordIds: assignment.vocabularyIds || []
                                    });
                                    setIsAssignmentModalOpen(true);
                                }} 
                                className="bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 px-6 rounded-lg shadow-md transition-colors text-sm"
                            >
                                Edit Assignment
                            </button>
                            <button 
                                onClick={() => openConfirm('delete_assignment', assignment.id, 'Delete', `Delete assignment "${assignment.title}"?`, true)} 
                                className="ml-auto text-red-300 hover:text-red-500 px-3 transition-colors"
                            >
                                <AppIcon name="trash-alt" />
                            </button>
                        </div>
                    </motion.div>
                );
            }) : <div className="text-center text-gray-600 p-8">No assignments match your filter.</div>}
            
            {totalPages > 1 && <div className="flex justify-center mt-4"><Pagination currentPage={assignmentPage} totalPages={totalPages} onPageChange={setAssignmentPage} totalItems={totalItems} /></div>}
        </div>
    );
  };

  // ------------------------------------
  // Modals Content
  // ------------------------------------

  // 1. Class Details Modal
  const renderClassDetails = () => {
    if (!viewingClass) return null;
    const classStudents = students.filter(s => s.classIds && s.classIds.includes(viewingClass.id));
    const stats = getClassStats(viewingClass.id);

    return (
      <div>
        {/* Top Stats Banner */}
        <div className="bg-sky-50 rounded-xl p-6 mb-6 flex justify-around text-center border border-sky-100">
            <div>
                <div className="text-3xl font-bold text-slate-800">{stats.count}</div>
                <div className="text-xs text-slate-500 uppercase">Total Students</div>
            </div>
            <div>
                <div className="text-3xl font-bold text-slate-800">{stats.avgProgress}%</div>
                <div className="text-xs text-slate-500 uppercase">Average Progress</div>
            </div>
            <div>
                <div className="text-3xl font-bold text-slate-800">{stats.activeAssigns}</div>
                <div className="text-xs text-slate-500 uppercase">Active Assignments</div>
            </div>
        </div>

        <h4 className="text-lg font-bold text-slate-800 mb-4">Students in {viewingClass.name}</h4>
        
        {/* Student List */}
        <div className="bg-sky-100/30 rounded-xl p-2 space-y-2 max-h-[50vh] overflow-y-auto">
          {classStudents.length > 0 ? classStudents.map(student => (
             <div key={student.id} className="bg-white rounded-lg p-4 flex justify-between items-center shadow-sm border border-sky-50">
                 <div>
                     <h4 className="font-bold text-slate-800 text-base">{student.name}</h4>
                     <p className="text-slate-400 text-xs mt-1">Last active: {student.lastActive}</p>
                 </div>
                 <div className="text-right">
                     <div className="text-slate-800 font-bold text-base mb-1">{student.wordsLearned} <span className="text-xs font-normal text-slate-500">words</span></div>
                     <div className={`inline-block px-2 py-0.5 rounded-full text-white text-[10px] font-bold ${student.accuracy>=90?'bg-green-500':'bg-yellow-500'}`}>
                         {student.accuracy}% accuracy
                     </div>
                 </div>
             </div>
          )) : <div className="text-center text-slate-500 py-6">No students enrolled.</div>}
        </div>
        
        <div className="flex justify-end pt-4">
            <button onClick={() => setViewingClass(null)} className="px-6 py-2 bg-gray-200 rounded-lg font-bold text-slate-700 hover:bg-gray-300">Close</button>
        </div>
      </div>
    );
  };

  // 2. Add Class Form
  const renderAddClassForm = () => (
    <form className="space-y-6" onSubmit={handleCreateClass}>
      <div><label className="block font-bold mb-1 text-slate-700">Class Name</label><input className="w-full px-4 py-2 border rounded-lg" value={classForm.name} onChange={e=>setClassForm({...classForm, name:e.target.value})} required placeholder="e.g. Year 5A" /></div>
      <div><label className="block font-bold mb-1 text-slate-700">Description</label><textarea className="w-full px-4 py-2 border rounded-lg" value={classForm.description} onChange={e=>setClassForm({...classForm, description:e.target.value})} placeholder="Optional description..." /></div>
      <div><label className="block font-bold mb-1 text-slate-700">Year</label><select className="w-full px-4 py-2 border rounded-lg" value={classForm.academicYear} onChange={e=>setClassForm({...classForm, academicYear:e.target.value})}>
        {/* 🟢 动态渲染年份 */}
              {academicYearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
              ))}
        </select></div>
      
      {/* 🟢 Submit Button with Loading */}
      <div className="flex justify-end gap-3 pt-4">
          <button type="button" onClick={()=>setIsClassModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-lg font-bold">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-green-500 text-white rounded-lg font-bold flex items-center justify-center min-w-[140px] disabled:opacity-70 disabled:cursor-not-allowed">
              {isSubmitting ? <><AppIcon name="spinner" className="mr-2" /> Creating...</> : 'Create Class'}
          </button>
      </div>
    </form>
  );

  // 3. Edit Class Modal
  const renderEditClassModal = () => {
    if (!editingClass) return null;
    
    // 🟢 处理点击联想项
    const handleSelectSuggestion = (student) => {
        setTempClassStudents(prev => [...prev, student]);
        setInviteEmail(''); // 清空输入
        setStudentSuggestions([]); // 清空建议
    };

    return (
      <div className="space-y-6">
        {/* ... (Class Name, Description, Year 部分保持不变) ... */}
        <div><label className="block font-bold mb-1 text-slate-700">Class Name</label><input className="w-full px-4 py-3 border border-slate-300 rounded-lg" value={editClassForm.name} onChange={e => setEditClassForm({...editClassForm, name: e.target.value})} /></div>
        <div><label className="block font-bold mb-1 text-slate-700">Description</label><textarea className="w-full px-4 py-3 border border-slate-300 rounded-lg" value={editClassForm.description} onChange={e => setEditClassForm({...editClassForm, description: e.target.value})} /></div>
        <div><label className="block font-bold mb-1 text-slate-700">Academic Year</label><select className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-white" value={editClassForm.academicYear} onChange={e => setEditClassForm({...editClassForm, academicYear: e.target.value})}>
            {/* 🟢 动态渲染年份 */}
                {academicYearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                ))}
            </select></div>
        
        <div className="bg-sky-100/30 p-5 rounded-xl border border-sky-100">
            <h4 className="font-bold mb-4 text-slate-700">Student Management ({tempClassStudents.length})</h4>
            
            {/* 已添加的学生列表 */}
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto custom-scrollbar bg-white rounded-lg p-2 border border-sky-100">
                {tempClassStudents.map(student => (
                    <div key={student.id} className="flex justify-between items-center bg-blue-50 p-2 rounded border border-blue-100">
                        <span className="text-slate-700 text-sm font-medium">{student.name} <span className="text-xs text-gray-500">({student.email})</span></span>
                        <button onClick={() => handleRemoveFromTempList(student.id)} className="text-red-500 hover:text-red-700 text-xs font-bold px-2">Remove</button>
                    </div>
                ))}
            </div>

            {/* 🟢 智能搜索输入框 */}
            <div className="relative">
                <div className="flex gap-2">
                    <input 
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" 
                        placeholder="Type at least 4 letters of email..." 
                        value={inviteEmail} 
                        onChange={e => setInviteEmail(e.target.value)} 
                    />
                    {/* 保留手动添加按钮作为兜底 */}
                    <button onClick={handleAddStudentToTempList} className="bg-green-500 hover:bg-green-600 text-white font-bold px-4 py-2 rounded-lg text-sm">Add</button>
                </div>

                {/* 🟢 下拉联想菜单 */}
                {inviteEmail.length >= 4 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto">
                        {isSearchingStudents ? (
                            <div className="p-3 text-center text-gray-500 text-sm"><AppIcon name="spinner" className="mr-2" />Searching...</div>
                        ) : studentSuggestions.length > 0 ? (
                            studentSuggestions.map(student => (
                                <div 
                                    key={student.id} 
                                    onClick={() => handleSelectSuggestion(student)}
                                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                                >
                                    <div className="font-bold text-slate-700 text-sm">{student.email}</div>
                                    <div className="text-xs text-slate-500">{student.name || 'No Name'}</div>
                                </div>
                            ))
                        ) : (
                            <div className="p-3 text-center text-gray-400 text-xs italic">No matching students found</div>
                        )}
                    </div>
                )}
            </div>
            <p className="text-xs text-slate-400 mt-2 ml-1">
                * Start typing an email address to see suggestions from the database.
            </p>
        </div>
        
        {/* Submit Button */}
        <div className="flex justify-between items-center pt-4 border-t border-slate-200">
            <button onClick={handleSaveEditClass} disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2.5 rounded-lg flex items-center justify-center min-w-[150px] disabled:opacity-70 disabled:cursor-not-allowed">
                {isSubmitting ? <><AppIcon name="spinner" className="mr-2" /> Saving...</> : 'Save Changes'}
            </button>
            <button onClick={() => setEditingClass(null)} className="text-slate-500 font-bold px-4">Cancel</button>
        </div>
      </div>
    );
  };

  // 4. Assignment Form
  const renderAssignmentForm = (isEdit) => {
      const availableWords = vocabList.filter(v => {
          if (!wordSearchTerm) return false;
          return v.word.toLowerCase().includes(wordSearchTerm.toLowerCase());
      });
      const selectedWords = vocabList.filter(v => assignmentForm.selectedWordIds.includes(v.id));

      return (
        <form className="space-y-6" onSubmit={handleSaveAssignment}>
            <div><label className="block font-bold mb-1">Title</label><input className="w-full px-4 py-2 border rounded-lg" value={assignmentForm.title} onChange={e=>setAssignmentForm({...assignmentForm, title:e.target.value})} required placeholder="e.g. Week 3 Core" /></div>
            
            <div className="grid grid-cols-2 gap-4">
                <div><label className="block font-bold mb-1">Class</label><select className="w-full px-4 py-2 border rounded-lg" value={assignmentForm.classId} onChange={e=>setAssignmentForm({...assignmentForm, classId:e.target.value})} required disabled={isEdit}><option value="">Select Class</option>{classes.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div><label className="block font-bold mb-1">Due Date</label><input type="date" className="w-full px-4 py-2 border rounded-lg" value={assignmentForm.dueDate} onChange={e=>setAssignmentForm({...assignmentForm, dueDate:e.target.value})} required /></div>
            </div>

            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <label className="block font-bold mb-2">Select Words ({assignmentForm.selectedWordIds.length})</label>
                <input className="w-full px-3 py-2 border rounded-lg mb-2 text-sm" placeholder="Search vocabulary to add..." value={wordSearchTerm} onChange={e => setWordSearchTerm(e.target.value)} />
                {wordSearchTerm && (
                    <div className="bg-white border rounded-lg max-h-32 overflow-y-auto mb-4 shadow-sm p-2">
                        {availableWords.length > 0 ? availableWords.map(w => (
                            <div key={w.id} className="flex justify-between items-center p-2 hover:bg-gray-50 cursor-pointer" onClick={() => toggleWordInAssignment(w.id)}>
                                <span className="font-bold text-sm">{w.word}</span>
                                {assignmentForm.selectedWordIds.includes(w.id) ? <AppIcon name="check" className="text-green-500" /> : <AppIcon name="plus" className="text-blue-500" />}
                            </div>
                        )) : <div className="text-gray-400 text-sm p-2">No matching words.</div>}
                    </div>
                )}
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {selectedWords.map(w => (
                        <span key={w.id} className="bg-white border border-blue-200 text-blue-700 px-3 py-1 rounded-full text-sm font-bold flex items-center">
                            {w.word}
                            <AppIcon name="times" className="ml-2 cursor-pointer text-red-400 hover:text-red-600" onClick={() => toggleWordInAssignment(w.id)} />
                        </span>
                    ))}
                    {selectedWords.length === 0 && <span className="text-gray-400 text-sm italic">No words selected yet.</span>}
                </div>
            </div>

            <div><label className="block font-bold mb-1">Instructions</label><textarea className="w-full px-4 py-2 border rounded-lg" value={assignmentForm.instructions} onChange={e=>setAssignmentForm({...assignmentForm, instructions:e.target.value})} placeholder="Notes..." /></div>
            
            {/* 🟢 Submit Button with Loading */}
            <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={()=>{setIsAssignmentModalOpen(false); setEditingAssignment(null);}} className="px-4 py-2 bg-gray-200 rounded-lg font-bold">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-blue-500 text-white rounded-lg font-bold flex items-center justify-center min-w-[120px] disabled:opacity-70 disabled:cursor-not-allowed">
                    {isSubmitting ? (
                        <><AppIcon name="spinner" className="mr-2" /> {isEdit ? 'Updating...' : 'Creating...'}</>
                    ) : (
                        isEdit ? 'Update' : 'Create'
                    )}
                </button>
            </div>
        </form>
      );
  };

  // 5. Assignment Details
  const renderAssignmentDetails = () => {
      if(!viewingAssignment) return null;
      const vocabListNames = (viewingAssignment.vocabularyIds||[]).map(id=>vocabMap[id]||'Unknown');
      const totalStudents = getClassStudentCountById(viewingAssignment.classId);
      const completedCount = viewingAssignment.completedCount || 0;
      const progressPercent = totalStudents > 0 ? Math.round((completedCount / totalStudents) * 100) : 0;
      const className = getClassNameById(viewingAssignment.classId);
      const dueDateStr = toDateInputValue(viewingAssignment.dueDate);

      return (
        <div className="space-y-6">
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="text-2xl font-bold text-slate-800">{viewingAssignment.title}</h3>
                    <p className="text-slate-500 font-bold">{className}</p>
                    <p className="text-xs text-slate-400">Due: {dueDateStr}</p>
                </div>
                <div className="text-right">
                    <div className="text-2xl font-bold text-slate-800">{completedCount}/{totalStudents}</div>
                    <div className="text-xs text-slate-500 uppercase">Completed</div>
                </div>
            </div>
            <div className="space-y-1">
                <div className="flex justify-between text-xs text-slate-500"><span>Progress</span><span>{progressPercent}%</span></div>
                <div className="w-full bg-blue-100 h-2.5 rounded-full overflow-hidden"><div className="bg-blue-600 h-full rounded-full" style={{ width: `${progressPercent}%` }}></div></div>
            </div>
            <div className="bg-sky-50 rounded-xl p-4 border border-sky-100">
                <h4 className="font-bold text-slate-700 mb-3">Vocabulary Words ({vocabListNames.length})</h4>
                <div className="flex flex-wrap gap-2">{vocabListNames.map((w,i)=><span key={i} className="bg-white text-slate-700 px-3 py-1.5 rounded-lg text-sm font-bold border border-slate-200 shadow-sm">{w}</span>)}</div>
            </div>
            <div>
                <h4 className="font-bold text-slate-800 mb-3">Student Progress</h4>
                <div className="bg-sky-100/30 rounded-xl p-2 space-y-2 max-h-[40vh] overflow-y-auto">
                    {loadingSubmissions ? <div className="text-center py-4 text-slate-500"><AppIcon name="spinner" className="mr-2" />Loading...</div> : 
                    currentAssignmentSubmissions.length > 0 ? currentAssignmentSubmissions.map((sub, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-4 flex justify-between items-center shadow-sm border border-sky-50">
                            <div><h5 className="font-bold text-slate-800">{sub.userName}</h5><p className="text-xs text-slate-400">Submitted: {firestoreToDate(sub.submittedAt)?.toLocaleDateString() || '—'}</p></div>
                            <div className="text-right"><div className="font-bold text-slate-800">{sub.accuracy}%</div><span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Completed</span></div>
                        </div>
                    )) : students.filter(s => s.classIds?.includes(viewingAssignment.classId)).map(s => (
                        <div key={s.id} className="bg-white rounded-lg p-4 flex justify-between items-center shadow-sm border border-sky-50 opacity-70">
                            <div><h5 className="font-bold text-slate-800">{s.name}</h5><p className="text-xs text-slate-400">Not submitted</p></div>
                            <div className="text-right"><span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Pending</span></div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex justify-end pt-2"><button onClick={()=>setViewingAssignment(null)} className="px-6 py-2 bg-gray-200 rounded-lg font-bold text-slate-700">Close</button></div>
        </div>
      );
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-sky-100"><div className="text-2xl font-bold text-blue-600"><AppIcon name="spinner" className="mr-3" />Loading Dashboard...</div></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
        <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center mb-2">
                    <img src="https://img.icons8.com/color/96/classroom.png" alt="Teacher" className="w-12 h-12 mr-3" />
                    <h1 className="text-4xl font-extrabold text-slate-800 font-kid tracking-wide">TEACHER DASHBOARD</h1>
                </div>
                <p className="text-xl text-slate-700 font-medium">Welcome back, {user?.name}!</p>
                <p className="text-slate-500">{schoolName}</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">{[{id:'classes', name:'My Classes', icon: 'users'},{id:'students', name:'Student Progress', icon: 'chart-line'},{id:'assignments', name:'Assignments', icon: 'tasks'}].map(tab => (
                <button key={tab.id} onClick={()=>setActiveTab(tab.id)} className={`px-8 py-3 rounded-full font-bold transition-all flex items-center shadow-sm ${activeTab===tab.id ? 'bg-white text-purple-600 shadow-md scale-105 ring-2 ring-purple-100' : 'bg-white/40 text-slate-600 hover:bg-white/60'}`}><AppIcon name={tab.icon} className="mr-2" /> {tab.name}</button>
            ))}</div>
            
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                {activeTab === 'classes' && renderClassesView()}
                {activeTab === 'students' && renderStudentsView()}
                {activeTab === 'assignments' && renderAssignmentsView()}
            </motion.div>
        </div>
        
        <Modal isOpen={isClassModalOpen} onClose={()=>setIsClassModalOpen(false)} title="Create New Class">{renderAddClassForm()}</Modal>
        <Modal isOpen={isAssignmentModalOpen} onClose={()=>setIsAssignmentModalOpen(false)} title="Create Assignment">{renderAssignmentForm(false)}</Modal>
        <Modal isOpen={!!editingClass} onClose={()=>setEditingClass(null)} title={`Edit Class: ${editingClass?.name}`}>{renderEditClassModal()}</Modal>
        <Modal isOpen={!!editingAssignment} onClose={()=>{setEditingAssignment(null); setIsAssignmentModalOpen(false);}} title="Edit Assignment">{renderAssignmentForm(true)}</Modal>
        <Modal isOpen={!!viewingClass} onClose={()=>setViewingClass(null)} title="Class Details">{renderClassDetails()}</Modal>
        <Modal isOpen={!!viewingAssignment} onClose={()=>setViewingAssignment(null)} title="Assignment Details">{renderAssignmentDetails()}</Modal>
        <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={()=>setConfirmDialog({...confirmDialog,isOpen:false})} onConfirm={handleConfirmAction} title={confirmDialog.title} message={confirmDialog.message} isLoading={isActionLoading} isDestructive={confirmDialog.isDestructive} />
    </div>
  );
};

export default TeacherPage;